/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */


import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { AppProvider, useApp } from './contexts/AppContext';
import { Settings } from './pages/Settings';
import { UserStories } from './pages/UserStories';
import { Issues } from './pages/Issues';
import { Products } from './pages/Products';
import { Releases } from './pages/Releases';
import { Dashboard } from './pages/Dashboard';
import { Help } from './pages/Help';
import { AuthCheck } from './components/AuthCheck';
import { AuthProvider, useAuth } from '@shared/contexts/AuthContext';
import { WorkOSProvider } from '@shared/components/auth';
import { AuthenticatedChatProvider } from '@shared/contexts/ChatContext/AuthenticatedChatProvider';
import { useChatContext } from '@shared/contexts/ChatContext';
import { AgentSidebar } from '@shared/components/agent/AgentSidebar';
import { ChatHistoryPanel } from '@shared/components/agent/ChatHistoryPanel';
import { Toaster } from './components/ui/toaster';
import { useToast } from './components/ui/use-toast';
import { Settings as SettingsIcon, FileText, Home, Bug, Package, Rocket, GitBranch, HelpCircle, Cloud, Bot, BookOpen, GanttChart, PanelLeftClose, PanelLeft } from 'lucide-react';
import { ChatSession } from '@shared/schema/chat';
import { UserProfile } from './pages/UserProfile';
import SignInPage from './pages/SignIn';
import SignUpPage from './pages/SignUp';
import { Changes } from './pages/Changes';
import { Deployments } from './pages/Deployments';
import { NewProject } from './pages/NewProject';
import { Agents } from './pages/Agents';
import { Playbooks } from './pages/Playbooks';
import { Project } from './pages/Project';
import { LandingPage } from './pages/marketing/LandingPage';
import { DownloadPage } from './pages/marketing/DownloadPage';
import { ChangelogPage } from './pages/marketing/ChangelogPage';
import { OBinDownloadPage } from './pages/marketing/ObinDownloadPage';
import { createQueryOptions } from '@shared/lib/api';
import { getOrganizationSettings } from '@shared/lib/api/organization_settings';
import type { UserProfileResponse } from '@shared/types/settings';
import type { VelocitySettings } from '@shared/types/organizationSettings';
import { DEFAULT_VERSION } from '@shared/config/version';
import { useCallback, useEffect, useState } from 'react';
import { WalkthroughProvider } from './contexts/WalkthroughContext';
import { PrerequisitesDialog, WalkthroughOverlay } from './components/walkthrough';

// White-label build flag - defined in vite.config.ts
// Build with: OBIN_BUILD=true npm run electron:build:mac:dev
declare const __OBIN_BUILD__: boolean;

// Create QueryClient instance for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function Navigation({ isCollapsed, onToggleCollapse }: { isCollapsed: boolean; onToggleCollapse: () => void }) {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const { data: profileData } = useQuery(
    createQueryOptions<UserProfileResponse>('/api/users/profile')
  );
  
  // Fetch organization settings for showDashboard preference
  const { data: orgSettings } = useQuery(getOrganizationSettings());
  
  // Get showDashboard setting with default false
  const showDashboard = (orgSettings?.velocitySettings as VelocitySettings | undefined)?.showDashboard ?? false;
  
  // Fetch version from server
  const { data: versionData } = useQuery({
    queryKey: ['version'],
    queryFn: async () => {
      const response = await fetch('http://localhost:3002/api/version');
      if (!response.ok) {
        throw new Error('Failed to fetch version');
      }
      const data = await response.json();
      return data.data;
    },
    staleTime: Infinity, // Version doesn't change during runtime
  });

  const avatarUrl =
    (profileData?.avatar?.trim() || null) ??
    (user?.photoUrl || null) ??
    ((user && (user as unknown as { avatar?: string }).avatar) || '');
  const displayName =
    profileData?.name?.trim() ||
    user?.name ||
    (isLoading ? 'Loading...' : 'Your profile');

  const getInitials = (name: string) => {
    if (!name.trim()) return 'U';
    const parts = name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '');
    return parts.join('') || 'U';
  };

  return (
    <div
      className={`sidebar ${isCollapsed ? 'sidebar-collapsed' : ''}`}
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        width: isCollapsed ? '60px' : '250px',
        transition: 'width 0.2s ease-in-out',
        overflow: 'hidden',
        position: 'relative'
      }}
      data-walkthrough="sidebar-nav"
    >
      {/* Collapse Toggle Button - Upper Right */}
      <button
        onClick={onToggleCollapse}
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        style={{
          position: 'absolute',
          top: '16px',
          right: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          padding: 0,
          background: 'transparent',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          color: '#999',
          transition: 'color 0.2s, background-color 0.2s',
          zIndex: 1
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f0f0f0';
          e.currentTarget.style.color = '#666';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#999';
        }}
      >
        {isCollapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
      </button>

      <div style={{ marginBottom: '32px', paddingRight: isCollapsed ? '0' : '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {__OBIN_BUILD__ && !isCollapsed && (
            <img 
              src={`${import.meta.env.BASE_URL}assets/obin_logo.png`} 
              alt="Obin" 
              style={{ height: '24px', width: 'auto' }} 
            />
          )}
          {!isCollapsed ? (
            <>
              <h1
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#0066cc',
                  margin: 0,
                  whiteSpace: 'nowrap'
                }}>
                Velocity
              </h1>
            </>
          ) : (
            <h1
              style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#0066cc',
                margin: 0,
                textAlign: 'center',
                width: '100%'
              }}>
              V
            </h1>
          )}
        </div>
        {!isCollapsed && (
          <p style={{ 
            fontSize: '12px', 
            color: '#666', 
            margin: '4px 0 0 0',
            whiteSpace: 'nowrap'
          }}>
          Agentic Engineering for Products
          </p>
        )}
      </div>

      <nav style={{ flex: 1 }}>
        <ul className="sidebar-nav">
          {showDashboard && (
            <li>
              <Link 
                to="/dashboard" 
                className={location.pathname === '/dashboard' ? 'active' : ''}
                data-walkthrough="nav-dashboard"
                title={isCollapsed ? 'Dashboard' : undefined}
                style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
              >
                <Home size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
                {!isCollapsed && 'Dashboard'}
              </Link>
            </li>
          )}
          <li>
            <Link 
              to="/products" 
              className={location.pathname === '/products' ? 'active' : ''}
              data-walkthrough="nav-products"
              title={isCollapsed ? 'Products' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <Package size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'Products'}
            </Link>
          </li>
          <li>
            <Link 
              to="/releases" 
              className={location.pathname === '/releases' ? 'active' : ''}
              data-walkthrough="nav-releases"
              title={isCollapsed ? 'Releases' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <Rocket size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'Releases'}
            </Link>
          </li>
          <li>
            <Link 
              to="/project" 
              className={location.pathname === '/project' ? 'active' : ''}
              data-walkthrough="nav-project"
              title={isCollapsed ? 'Project' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <GanttChart size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'Project'}
            </Link>
          </li>
          <li>
            <Link 
              to="/user-stories" 
              className={location.pathname === '/user-stories' ? 'active' : ''}
              data-walkthrough="nav-user-stories"
              title={isCollapsed ? 'User Stories' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <FileText size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'User Stories'}
            </Link>
          </li>
          <li>
            <Link 
              to="/issues" 
              className={location.pathname === '/issues' || location.pathname === '/' ? 'active' : ''}
              data-walkthrough="nav-issues"
              title={isCollapsed ? 'Issues' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <Bug size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'Issues'}
            </Link>
          </li>
          <li>
            <Link 
              to="/changes" 
              className={location.pathname === '/changes' ? 'active' : ''}
              data-walkthrough="nav-changes"
              title={isCollapsed ? 'Changes' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <GitBranch size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'Changes'}
            </Link>
          </li>
          <li>
            <Link 
              to="/agents" 
              className={location.pathname === '/agents' ? 'active' : ''}
              data-walkthrough="nav-agents"
              title={isCollapsed ? 'Agents' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <Bot size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'Agents'}
            </Link>
          </li>
          <li>
            <Link 
              to="/playbooks" 
              className={location.pathname === '/playbooks' ? 'active' : ''}
              data-walkthrough="nav-playbooks"
              title={isCollapsed ? 'Playbooks' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <BookOpen size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'Playbooks'}
            </Link>
          </li>
          {/*
          <li>
            <Link 
              to="/deployments" 
              className={location.pathname === '/deployments' ? 'active' : ''}
              data-walkthrough="nav-deployments"
              title={isCollapsed ? 'Deployments' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <Cloud size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'Deployments'}
            </Link>
          </li>
          */}
          <li>
            <Link 
              to="/settings" 
              className={location.pathname === '/settings' ? 'active' : ''}
              data-walkthrough="nav-settings"
              title={isCollapsed ? 'Settings' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <SettingsIcon size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'Settings'}
            </Link>
          </li>
          <li>
            <Link 
              to="/help" 
              className={location.pathname === '/help' ? 'active' : ''}
              data-walkthrough="nav-help"
              title={isCollapsed ? 'Help' : undefined}
              style={isCollapsed ? { justifyContent: 'center', padding: '12px' } : undefined}
            >
              <HelpCircle size={16} style={{ marginRight: isCollapsed ? 0 : '12px', flexShrink: 0 }} />
              {!isCollapsed && 'Help'}
            </Link>
          </li>
        </ul>
      </nav>

      <div
        style={{
          marginTop: 'auto',
          paddingTop: '16px',
          borderTop: '1px solid #e6e6e6'
        }}
      >
        <Link
          to="/profile"
          className={location.pathname === '/profile' ? 'active' : ''}
          title={isCollapsed ? displayName : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            padding: isCollapsed ? '12px 8px' : '12px 16px',
            textDecoration: 'none',
            color: location.pathname === '/profile' ? '#ffffff' : '#333333',
            borderRadius: '4px',
            transition: 'background-color 0.2s',
            backgroundColor: location.pathname === '/profile' ? '#0066cc' : 'transparent'
          }}
        >
          <div
            style={{
              width: isCollapsed ? '28px' : '36px',
              height: isCollapsed ? '28px' : '36px',
              borderRadius: '50%',
              overflow: 'hidden',
              backgroundColor: '#d9e3f8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1f3a93',
              fontWeight: 600,
              marginRight: isCollapsed ? 0 : '12px',
              flexShrink: 0,
              fontSize: isCollapsed ? '11px' : '14px'
            }}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              getInitials(displayName)
            )}
          </div>
          {!isCollapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <span style={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {displayName}
              </span>
              <span style={{ fontSize: '12px', color: location.pathname === '/profile' ? '#e6f0ff' : '#666666' }}>
                View profile
              </span>
            </div>
          )}
        </Link>
        
        {/* Version Display */}
        {!isCollapsed && (
          <div
            style={{
              padding: '12px 16px',
              fontSize: '12px',
              color: '#999999',
              textAlign: 'left'
            }}
          >
            {versionData?.version ? `v${versionData.version}` : `v${DEFAULT_VERSION}`}
          </div>
        )}
      </div>
    </div>
  );
}

function ContentArea({ children }: { children: React.ReactNode }) {
  const { loadSession, setExpanded } = useChatContext();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Function to handle when a chat session is selected
  const handleChatSessionSelected = async (session: ChatSession) => {
    try {
      await loadSession(session.id);
      
      // Auto-open the agent sidebar if it's not already open
      setExpanded(true);
      
      toast({
        title: "Chat Session Loaded",
        description: `Loaded session with ${session.messageCount} message${session.messageCount !== 1 ? 's' : ''}`,
      });
    } catch (error) {
      console.error("Error loading chat session:", error);
      toast({
        title: "Error",
        description: "Failed to load chat session",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="flex h-full">
      {/* Agent Sidebar */}
      <AgentSidebar onNavigate={navigate} />
      
      {/* Main Page Content */}
      <div className="flex-1 overflow-auto" style={{ padding: '20px' }}>
        {children}
      </div>

      {/* Chat History Panel - Right side */}
      <ChatHistoryPanel onItemSelect={handleChatSessionSelected} />
    </div>
  );
}

function AppContent() {
  const { activeProduct } = useApp();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('velocity-sidebar-collapsed');
    return saved === 'true';
  });
  
  // Fetch organization settings for showDashboard preference
  const { data: orgSettings } = useQuery(getOrganizationSettings());
  
  // Get showDashboard setting with default false
  const showDashboard = (orgSettings?.velocitySettings as VelocitySettings | undefined)?.showDashboard ?? false;
  
  // Define context provider that supplies Velocity-specific context
  const agentContextProvider = useCallback(() => {
    const context: Record<string, any> = {};
    
    // Add active product information
    // Using spread operator to automatically include all product fields
    if (activeProduct) {
      context.activeProduct = {
        ...activeProduct,
      };
    }
        
    return context;
  }, [activeProduct]);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('velocity-sidebar-collapsed', String(newValue));
      return newValue;
    });
  }, []);

  return (
    <AuthenticatedChatProvider contextProvider={agentContextProvider}>
      <WalkthroughProvider>
        <AuthenticatedRootRedirect showDashboard={showDashboard} />
        <div className="flex h-screen">
          {/* Navigation Sidebar */}
          <Navigation isCollapsed={isSidebarCollapsed} onToggleCollapse={handleToggleSidebar} />
          
          {/* Main Content Area */}
          <main className="flex-1 flex flex-col overflow-hidden" style={{ transition: 'margin-left 0.2s ease-in-out' }}>
            <div className="flex-1 overflow-y-auto">
              <ContentArea>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/releases" element={<Releases />} />
                  <Route path="/project" element={<Project />} />
                  <Route path="/user-stories" element={<UserStories />} />
                  <Route path="/issues" element={<Issues />} />
                  <Route path="/changes" element={<Changes />} />
                <Route path="/agents" element={<Agents />} />
                <Route path="/playbooks" element={<Playbooks />} />
                <Route path="/deployments" element={<Deployments />} />
                <Route path="/new-project" element={<NewProject />} />
                <Route path="/profile" element={<UserProfile />} />
                  <Route path="/help" element={<Help />} />
                </Routes>
              </ContentArea>
            </div>
          </main>
        </div>
        
        {/* Walkthrough Components */}
        <PrerequisitesDialog />
        <WalkthroughOverlay />
      </WalkthroughProvider>
    </AuthenticatedChatProvider>
  );
}

// Component to handle root path redirect based on org settings
function AuthenticatedRootRedirect({ showDashboard }: { showDashboard: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Only redirect if we're at the exact root path
    if (location.pathname === '/') {
      // Redirect to dashboard or products based on org setting
      navigate(showDashboard ? '/dashboard' : '/products', { replace: true });
    }
  }, [location.pathname, navigate, showDashboard]);
  
  return null;
}

function App() {
  // Use Vite's base path from import.meta.env.BASE_URL
  // Ensure it ends without trailing slash for React Router
  const basename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';
  
  return (
    <QueryClientProvider client={queryClient}>
      <WorkOSProvider
        clientId={import.meta.env.VITE_WORKOS_CLIENT_ID}
        redirectUri={import.meta.env.VITE_WORKOS_REDIRECT_URI}
      >
        <AuthProvider>
          <Router basename={basename}>
            <Routes>
              {/* Public marketing routes */}
              <Route path="/home" element={<LandingPage />} />
              <Route path="/home/download" element={<DownloadPage />} />
              <Route path="/home/changelog" element={<ChangelogPage />} />
              <Route path="/home/obin/download" element={<OBinDownloadPage />} />
              
              {/* Public auth routes */}
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              
              {/* Protected app routes */}
              <Route path="/*" element={
                <AuthCheck>
                  <AppProvider>
                    <AppContent />
                  </AppProvider>
                </AuthCheck>
              } />
            </Routes>
            <Toaster />
          </Router>
        </AuthProvider>
      </WorkOSProvider>
    </QueryClientProvider>
  );
}

export default App; 